/**
 * InlineEditProvider — ?mode=edit でダブルクリック編集を有効化する共通コンポーネント
 *
 * 使い方:
 * <InlineEditProvider slug="lp-story">
 *   <article>...本文...</article>
 * </InlineEditProvider>
 *
 * - 通常モード: KV から保存済み HTML を取得して反映（全ユーザーに反映される）
 * - ?mode=edit&token=XXX: ダブルクリックで対象要素を contentEditable に
 * - Enter/Esc/blur で確定 → KV 保存 + 右上トースト
 *
 * 永続化: Cloudflare KV 経由 / デフォルト API path: /api/inline-edit
 * 認証:   ?token=<INLINE_EDIT_TOKEN> を mutation の Authorization: Bearer に変換
 *
 * 別ストレージに差し替えたい場合は adapter prop を渡す。
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

const PRIMARY = "#037f40";
const DEFAULT_API = "/api/inline-edit";

export type SaveAdapter = {
  load: (slug: string) => Promise<string | null>;
  save: (slug: string, html: string) => Promise<void>;
  clear: (slug: string) => Promise<void>;
};

function getTokenFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("token");
}

export function createKvAdapter(apiPath = DEFAULT_API): SaveAdapter {
  return {
    load: async (slug) => {
      try {
        const res = await fetch(
          `${apiPath}?slug=${encodeURIComponent(slug)}`,
          { cache: "no-store" }
        );
        if (!res.ok) return null;
        const data = (await res.json()) as { html: string | null };
        return data.html ?? null;
      } catch {
        return null;
      }
    },
    save: async (slug, html) => {
      const token = getTokenFromUrl() ?? "";
      const res = await fetch(`${apiPath}?slug=${encodeURIComponent(slug)}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ html }),
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`save failed: ${res.status} ${msg}`);
      }
    },
    clear: async (slug) => {
      const token = getTokenFromUrl() ?? "";
      const res = await fetch(`${apiPath}?slug=${encodeURIComponent(slug)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`clear failed: ${res.status} ${msg}`);
      }
    },
  };
}

type Props = {
  slug: string;
  children: ReactNode;
  adapter?: SaveAdapter;
  /** 編集モード判定。default は ?mode=edit */
  isEditModeFromUrl?: () => boolean;
  /** デフォルト adapter が叩く API パス (default: /api/inline-edit) */
  apiPath?: string;
  /** ルート div に付与する className (高さチェーンを保ちたい場合等) */
  className?: string;
};

export function InlineEditProvider({
  slug,
  children,
  adapter,
  isEditModeFromUrl,
  apiPath,
  className,
}: Props) {
  const resolvedAdapter = useMemo(
    () => adapter ?? createKvAdapter(apiPath),
    [adapter, apiPath]
  );
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isEditModeFromUrl) {
      setEditMode(isEditModeFromUrl());
      return;
    }
    const params = new URLSearchParams(window.location.search);
    setEditMode(params.get("mode") === "edit");
  }, [isEditModeFromUrl]);

  // 保存済み HTML の流し込みは編集モード時のみ。
  // 通常閲覧で innerHTML を上書きすると、SSR で描画済みの本文（新セクション含む）が
  // 古い保存版に置換され、React の hydration と衝突して #418 でサブツリーごと消える。
  // 通常閲覧では常に最新の SSR 本文をそのまま使う。
  useEffect(() => {
    if (!editMode || !rootRef.current) return;
    let active = true;
    resolvedAdapter.load(slug).then((saved) => {
      if (active && saved && rootRef.current) {
        rootRef.current.innerHTML = saved;
      }
    });
    return () => {
      active = false;
    };
  }, [editMode, slug, resolvedAdapter]);

  useEffect(() => {
    if (!editMode || !rootRef.current) return;
    const el = rootRef.current;
    let saveTimer: ReturnType<typeof setTimeout> | null = null;
    let editingTarget: HTMLElement | null = null;

    const scheduleSave = () => {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = setTimeout(() => {
        if (rootRef.current) {
          resolvedAdapter
            .save(slug, rootRef.current.innerHTML)
            .then(() => {
              setSaveError(null);
              setSavedAt(Date.now());
            })
            .catch((e: unknown) => {
              const msg = e instanceof Error ? e.message : String(e);
              console.warn("inline-edit save failed:", msg);
              setSaveError(
                msg.includes("401")
                  ? "保存失敗。token が必要です (?token=...)"
                  : msg.includes("503")
                    ? "保存失敗。編集機能が設定されていません"
                    : `保存失敗。${msg}`,
              );
            });
        }
      }, 300);
    };

    const findEditable = (node: Node | null): HTMLElement | null => {
      let cur: Node | null = node;
      while (cur && cur !== el) {
        if (cur.nodeType === 1) {
          const e = cur as HTMLElement;
          const tag = e.tagName.toLowerCase();
          if (
            [
              "a",
              "button",
              "img",
              "svg",
              "path",
              "input",
              "details",
              "summary",
            ].includes(tag)
          )
            return null;
          if (
            [
              "p",
              "h1",
              "h2",
              "h3",
              "h4",
              "li",
              "span",
              "blockquote",
              "strong",
              "em",
            ].includes(tag)
          )
            return e;
        }
        cur = cur.parentNode;
      }
      return null;
    };

    const endEdit = () => {
      if (!editingTarget) return;
      editingTarget.removeAttribute("contenteditable");
      editingTarget.style.outline = "";
      editingTarget.style.background = "";
      editingTarget = null;
      scheduleSave();
    };

    const onDblClick = (e: MouseEvent) => {
      const target = findEditable(e.target as Node);
      if (!target) return;
      e.preventDefault();
      if (editingTarget && editingTarget !== target) endEdit();
      editingTarget = target;
      target.setAttribute("contenteditable", "true");
      target.style.outline = `2px solid ${PRIMARY}`;
      target.style.background = "rgba(5,180,91,0.05)";
      target.focus();
      const sel = window.getSelection();
      if (sel) {
        sel.selectAllChildren(target);
        sel.collapseToEnd();
      }
    };

    const onBlur = (e: FocusEvent) => {
      if (e.target === editingTarget) endEdit();
    };

    const onKey = (e: KeyboardEvent) => {
      if (!editingTarget) return;
      if (e.key === "Escape" || (e.key === "Enter" && !e.shiftKey)) {
        e.preventDefault();
        endEdit();
      }
    };

    const onInput = () => scheduleSave();

    el.addEventListener("dblclick", onDblClick);
    el.addEventListener("blur", onBlur, true);
    el.addEventListener("keydown", onKey);
    el.addEventListener("input", onInput);
    return () => {
      endEdit();
      el.removeEventListener("dblclick", onDblClick);
      el.removeEventListener("blur", onBlur, true);
      el.removeEventListener("keydown", onKey);
      el.removeEventListener("input", onInput);
      if (saveTimer) clearTimeout(saveTimer);
    };
  }, [editMode, slug, resolvedAdapter]);

  const handleReset = () => {
    if (
      typeof window === "undefined" ||
      !window.confirm("編集内容を全て破棄して、元の本文に戻しますか？")
    )
      return;
    resolvedAdapter.clear(slug).then(() => window.location.reload());
  };

  const handleCopy = () => {
    if (!rootRef.current) return;
    navigator.clipboard
      .writeText(rootRef.current.innerHTML)
      .then(() => window.alert("編集後HTMLをクリップボードにコピーしました"));
  };

  return (
    <>
      <div
        ref={rootRef}
        className={className}
        style={editMode ? { caretColor: PRIMARY, cursor: "text" } : undefined}
      >
        {children}
      </div>

      {editMode && (
        <>
          <SaveToast savedAt={savedAt} />
          <ErrorToast error={saveError} onDismiss={() => setSaveError(null)} />
          <div
            className="fixed bottom-4 right-4 z-[200] flex flex-col gap-2"
            style={{ alignItems: "flex-end" }}
          >
            <span
              style={{
                background: PRIMARY,
                color: "#fff",
                fontFamily:
                  "'Noto Sans JP',YakuHanJP,sans-serif",
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.08em",
                borderRadius: 999,
                padding: "5px 12px",
              }}
            >
              EDIT — ダブルクリックで編集
            </span>
            <button
              type="button"
              onClick={handleCopy}
              style={btnStyle("#fff", "#363635", "rgba(54,54,53,0.16)")}
            >
              HTMLをコピー
            </button>
            <button
              type="button"
              onClick={handleReset}
              style={btnStyle("#fff", "#b22629", "#b22629")}
            >
              編集をリセット
            </button>
          </div>
        </>
      )}
    </>
  );
}

function btnStyle(bg: string, color: string, border: string): React.CSSProperties {
  return {
    background: bg,
    color,
    border: `1px solid ${border}`,
    borderRadius: 999,
    padding: "6px 14px",
    fontFamily: "'Noto Sans JP',YakuHanJP,sans-serif",
    fontWeight: 700,
    fontSize: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
    cursor: "pointer",
  };
}

function SaveToast({ savedAt }: { savedAt: number | null }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (savedAt === null) return;
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 1800);
    return () => clearTimeout(t);
  }, [savedAt]);

  return (
    <div
      className="fixed top-4 right-4 z-[200] pointer-events-none"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(-6px)",
        transition: "opacity 200ms ease, transform 200ms ease",
      }}
    >
      <div
        className="flex items-center gap-2"
        style={{
          background: PRIMARY,
          color: "#fff",
          fontFamily:
            "'Noto Sans JP',YakuHanJP,sans-serif",
          fontWeight: 700,
          fontSize: 13,
          letterSpacing: "0.04em",
          borderRadius: 999,
          padding: "8px 14px",
          boxShadow: "0 4px 14px rgba(3,127,64,0.35)",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <path
            d="M3 8L7 12L13 4"
            stroke="#fff"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        保存しました
      </div>
    </div>
  );
}

function ErrorToast({
  error,
  onDismiss,
}: {
  error: string | null;
  onDismiss: () => void;
}) {
  if (!error) return null;
  return (
    <div
      className="fixed top-16 right-4 z-[200]"
      style={{
        background: "#b22629",
        color: "#fff",
        fontFamily:
          "'Noto Sans JP',YakuHanJP,sans-serif",
        fontWeight: 700,
        fontSize: 13,
        borderRadius: 12,
        padding: "10px 14px",
        boxShadow: "0 4px 14px rgba(178,38,41,0.35)",
        display: "flex",
        gap: 8,
        alignItems: "center",
        maxWidth: 360,
      }}
    >
      <span style={{ flex: 1 }}>{error}</span>
      <button
        type="button"
        onClick={onDismiss}
        style={{
          background: "transparent",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.4)",
          borderRadius: 999,
          padding: "2px 8px",
          fontSize: 11,
          cursor: "pointer",
        }}
      >
        ×
      </button>
    </div>
  );
}
